import { useMemo } from 'react'

import { CustomHeading, CustomText } from "../../../../../components/StyledComponents/styleComponents.index";

import { Button } from "@aidron/aidron-ds";

import PropTypes from "prop-types";

import { 
  OrderDetailsActions,
  OrderDetailsTable,
  OrderDetailsHeader,
} from "../../../../../components/SharedComponents/sharedComponents.index"

import { COLUMNS_IDENTIFIERS } from "../../../../../components/components.constants";

import { CameraIcon } from "../../../PurchaseHistory/purchaseHistory.index";

import { useSupplierConfirmationViewModel } from "../../../../../viewModel/useSuppliersConfirmationViewModel";
import { useOrderDetailsModalVM } from "../../../../../viewModel/useOrderDetailsModalViewModel";
import { isEligibleForPurchase, withOriginalValues } from '../../../../../viewModel/strategy/purchaseOrderEligibility/withOriginalValues/withOriginalValues';

import { UpdatingModal } from "../../../../../components/SharedComponents/UpdatingModal/UpdatingModal";

import { i18n } from "../../../../../translate/i18n";
import { HierarchyFiltersSalesRecommendation } from "../HierarchyFiltersSalesRecommendation/HierarchyFiltersSalesRecomendation";

import Modal from "react-modal";

const MODAL_STYLE = {
  OVERLAY: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(2px)'
  },

  DEFAULT: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px'
  }
};

/** Componente responsável pela exibição de informações relacionadas
 * ao fornecedor selecionado pelo usuário. É responsável pela renderização
 * de 1 fornecedor, múltiplos fornecedores ou exibição de alerta para
 * ausência de fornecedor (impede o prosseguimento do fluxo de compra
 * independente da opção - principal ou secundário)
 */
const SupplierSelection = ({ actions, ctx, uiState }) => {
  const { handleSelectChange } = actions || {};
  const { filteredDataSuppliers, uniqueSuppliers, labels, multipleSuppliersData } = ctx || {};
  const { selectedSupplierFromFilter, selectedSupplierId, canProceed } = uiState || {};

  const { validSuppliers, uniqueSuppliersInCurrentSelection } = multipleSuppliersData || {};

  if (selectedSupplierFromFilter || (filteredDataSuppliers?.length === 1 && selectedSupplierId !== null)) {
    return (
      <div style={{ padding: '1rem', borderRadius: '0.5rem', border: '0.0625rem solid #e5e7eb' }}>
        <CustomText fontSize={['0.7rem', '0.9rem', '1rem']} weight="bold" color="black">
          {labels.upperCardMessage}
        </CustomText>

        <select
          onChange={handleSelectChange}
          value={selectedSupplierId !== null ? selectedSupplierId : ''}
          className="w-full border border-gray-300 rounded px-3 py-2 bg-white text-gray-700 focus:outline-none focus:border-blue-500"
        >
          {uniqueSuppliers
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
            .map(({ name, fullName, id }) => (
              <option key={id} title={fullName} value={id}>
                {name}
              </option>
            ))}
        </select>

        <CustomText fontSize={['0.5rem', '0.7rem', '0.9rem']} weight="medium">
          {canProceed ? labels.lowerCardMessage : "A compra final dos produtos selecionados é 0"}
        </CustomText>
      </div>
    );
  }

  if (
    filteredDataSuppliers?.length > 1 &&
    filteredDataSuppliers.length !== uniqueSuppliers?.length &&
    selectedSupplierId === null
  ) {

    return (
      <div style={{ padding: '1rem', borderRadius: '0.5rem', border: '0.0625rem solid #e5e7eb' }}>
        <CustomText fontSize={['0.7rem', '0.9rem', '1rem']} weight="bold" color="black">
          {labels.upperCardMessage}
        </CustomText>

        <CustomText fontSize={['0.5rem', '0.7rem', '0.9rem']} weight="medium">
          {`Você está fazendo uma compra para ${validSuppliers.length} fornecedores:`}
        </CustomText>

        <ul>
          {uniqueSuppliersInCurrentSelection.map(supplier => (
            <li key={supplier.supplierId} style={{ fontSize: '0.8rem' }}>
              {supplier.supplierName}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <CustomText fontSize={['0.5rem', '0.7rem', '0.9rem']} weight="medium">
        {labels.notificationMessage}
      </CustomText>
    </div>
  );
};

/** Modal gerado após a geração de ordem de compra pelo usuário.
 * Tem como responsabilidade principal retornar para o usuário o
 * número do pedido, e possibilitar a limpeza dos filtros aplicados
 * na tabela de recomendações de compra.
 */
const OrderCreatedModal = ({ actions, ctx, uiState }) => {

  const { onClose } = actions || {};
  const { orderNumber, supplierName } = ctx || {};
  const { isOpen } = uiState || {};

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      style={{
        overlay: {
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 20000,
        },
        content: {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          transform: "translate(-50%, -50%)",
          padding: "2rem",
          textAlign: "center",
          borderRadius: "8px",
          minWidth: "320px"
        }
      }}
      contentLabel="Pedido criado"
    >
      <div>
        <h3 style={{ marginBottom: "1rem" }}>
          Ordem de pedido criada ✅
        </h3>

        <p style={{ fontSize: "1rem", color: "#5f5e5e" }}>
          Número do pedido:
        </p>

        <p style={{ 
          fontSize: "1.4rem", 
          fontWeight: 600,
          marginTop: "0.5rem",
          color: "#e75d0d"
        }}>
          #{orderNumber}
        </p>

        <p style={{ fontSize: "1rem", color: "#5f5e5e" }}>
          Fornecedor: <strong>{supplierName}</strong>
        </p>        

        <button
          style={{
            marginTop: "1.5rem",
            padding: "0.5rem 1.25rem",
            borderRadius: "6px",
            border: "none",
            backgroundColor: "#e75d0d",
            color: "#fff",
            fontWeight: 500,
            cursor: "pointer"
          }}
          onClick={onClose}
        >
          OK
        </button>
      </div>
    </Modal>
  );
};

/** Componente para renderização dos produtos elegíveis do 
 * fornecedor selecionado pelo usuário. É a última etapa
 * antes da geração de uma ordem de pedido.
 */
const OrderModal = ({ actions, ctx, uiState }) => {

  const { onClose, onOrderComplete } = actions || {}

  const { 
    orderData,
    prefixes,
    purchaseRecommendationDataOnly
  } = ctx || {};

  const {
    inputs,
    setInputs,
    showInputs,
    saveLoading
  } = uiState || {}

  const vmOrderModal = useOrderDetailsModalVM({
    actions: {
      onOrderComplete: onOrderComplete,
      onClose: onClose,
      setInputs: setInputs
    },
    ctx: {
      orderData: orderData,
      purchaseRecommendationDataOnly: purchaseRecommendationDataOnly,
      prefixes: prefixes
    },
    uiState: {
      inputs: inputs,
      showInputs: showInputs,
      saveLoading: saveLoading
    }
  });

  const { t } = i18n;

  const handleEdit = () => onClose();

  const getColumnStyles = (columnId) => {
    const textAlignColumn = [
      COLUMNS_IDENTIFIERS.PRODUCT_NAME
    ];
    const isMainSticky = textAlignColumn.includes(columnId);

    return {
      textAlign: isMainSticky ? 'left' : 'center',
    };
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(2px)'
      }} onClick={onClose}>
      <div 
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          maxHeight: '90vh',
          overflow: 'auto',
          margin: '20px'
        }} 
        id={`supplier-confirmation-modal`} 
        onClick={(e) => e.stopPropagation()}
      >

        {saveLoading && (
          <UpdatingModal 
            ctx={{ 
              count:Object.keys(inputs).length, 
              label: {
                primaryMessage: t("updatingMessage"),
                dynamicMessagesArr: [
                  t("updatingItems"), t("updatingItems_plural")
                ]
              }
            }}
            uiState={{ isOpen: saveLoading }}/>
        )}

        {vmOrderModal.uiState.isCreatingOrder && (
          <UpdatingModal 
            ctx={{ 
              count: vmOrderModal.ctx.products.length, 
              label: {
                primaryMessage: t("creatingOrder"),
                dynamicMessagesArr: [
                  t("updatingItems"), t("updatingItems_plural")
                ]
              }
            }}
            uiState={{ isOpen: vmOrderModal.uiState.isCreatingOrder }}/>
        )}      

        <OrderDetailsHeader 
          ctx={{
            upperLineInformation: vmOrderModal.ctx.upperLineInformation,
            bottomLineInformation: vmOrderModal.ctx.bottomLineInformation
          }}/>

        {vmOrderModal.uiState.hasMultipleSuppliers && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            margin: '0.5rem 0',
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <Button 
              onClick={vmOrderModal.actions.handlePrevSupplier} 
              label="Anterior" 
              disabled={vmOrderModal.ctx.currentSupplierIndex === 0} 
            />

            <span style={{ fontWeight: '500', color: '#374151' }}>
              {(() => {
                const remainingSuppliers = vmOrderModal.ctx.supplierIds.filter(id => !vmOrderModal.ctx.processedSupplierIds.includes(id));
                const currentDisplayIndex = remainingSuppliers.findIndex(id => id === vmOrderModal.ctx.currentSupplierId);
                return `Fornecedor ${currentDisplayIndex + 1} de ${remainingSuppliers.length}`;
              })()}
            </span>

            <Button 
              onClick={vmOrderModal.actions.handleNextSupplier} 
              label="Próximo" 
              disabled={vmOrderModal.ctx.currentSupplierIndex === vmOrderModal.ctx.supplierIds.length - 1}
            />
          </div>
        )}

        <OrderDetailsTable   
          ctx={{
            columns: vmOrderModal.ctx.columns,
            tableSourceData: vmOrderModal.ctx.products,
            tableSourceMetaData: {
              enableSorting: true,
              hideOnExport: [COLUMNS_IDENTIFIERS.PREFIX_NAME]
            }
          }}
          uiState={{
            getColumnStyles: getColumnStyles,
            cellStyle: { textAlign: 'left' },
            headerStyle: { textAlign: 'center' }
          }}/>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>

          <OrderDetailsActions 
            actions={{ createOrder: vmOrderModal.actions.orderCreation, handleEdit: handleEdit }}
            ctx={{
              suppliersAndProductRelations: [],
              currentSupplierName: vmOrderModal.ctx.currentSupplierName,
              storeName: orderData?.filteredData[0]?.storeInfo?.store?.name 
            }}            
            uiState={{
              customDisabledCondition: !!vmOrderModal.ctx.supplierIds.length,
              buttonsDisplayMode: "order_modal",
              showDownloadButtonForXLS: false,
              cameraIcon: CameraIcon,
            }}/>

        </div>
        
      </div>
    </div>
  );
};

/** Componente responsável por direcionar o fluxo 
 * do processo de compra principal do usuário.
 */

const PurchaseFlow = ({ actions, ctx, uiState }) => {
  const { 
    setAppliedFiltersCount, setSelectedProductIds, setSelectedSupplierFromFilter, 
    setShowFilters, handleClose, goToSupplierConfirmation,
    setAppliedLevels, setSelectedLevels, setTypedExpression, setAppliedExpression 
  } = actions || {};

  const { 
    productIds, 
    suppliersAndProductRelations, 
    hierarchyNames, 
    products, 
    filteredData 
  } = ctx || {};

  const { 
    appliedLevels, 
    selectedLevels, 
    typedExpression, 
    appliedExpression,
  } = uiState || {};

  const eligibleProductIds = useMemo(() => {
    const ids = new Set();
    (filteredData || []).forEach(item => {
      if (isEligibleForPurchase(item)) ids.add(item.productId);
    });
    return ids;
  }, [filteredData]);

  const eligibleProducts = useMemo(
    () => (products || []).filter(p => eligibleProductIds.has(p.productId)),
    [products, eligibleProductIds]
  );

  const eligibleSupplierRelations = useMemo(
    () => (suppliersAndProductRelations || []).filter(rel => eligibleProductIds.has(rel.productId)),
    [suppliersAndProductRelations, eligibleProductIds]
  );

  const eligibleProductIdsArray = useMemo(
    () => (productIds || []).filter(id => eligibleProductIds.has(id)),
    [productIds, eligibleProductIds]
  );

  return (
    <div style={MODAL_STYLE.OVERLAY}>
      <div style={MODAL_STYLE.DEFAULT}>
        <h4 style={{ marginBlockStart: "0.4rem", textDecoration: 'underline' }}>Instruções</h4>

        <section>
          <CustomHeading fontSize={["0.6rem", "0.8rem", "1rem"]} margin="0" inlineSize="100%">
            {"A) Selecione ao menos 1 Fornecedor, Subgrupo ou Produto para fazer o Pedido de Compra"}
          </CustomHeading>

          <p style={{
            fontFamily: 'Lato',
            fontWeight: 400,
            fontStyle: 'italic',
            fontSize: '0.9rem',
            lineHeight: '100%',
            letterSpacing: '0%',
            color: '#F16F22',
          }}>
            Para gerar pedidos, a Compra Final não pode estar zerada e o fornecedor não pode ser N/A.
          </p>

          <p style={{
            fontFamily: 'Lato',
            fontWeight: 400,
            fontStyle: 'italic',
            fontSize: '0.9rem',
            lineHeight: '100%',
            letterSpacing: '0%',
            color: '#F16F22',
          }}>
            Para estes casos, feche esta janela, localize o produto e insira estas informações no produto que deseja comprar
          </p>
        </section>

        <section style={{ padding: '1rem' }}>
          <HierarchyFiltersSalesRecommendation 
            actions={{
              setAppliedFiltersCount,
              setSelectedProductIds,
              setShowFilters,
              setSelectedSupplierFromFilter,
              setAppliedLevels,
              setSelectedLevels,
              setTypedExpression,
              setAppliedExpression
            }}
            ctx={{ 
              productIds: eligibleProductIdsArray, 
              suppliersAndProductRelations: suppliersAndProductRelations, 
              hierarchyNames: hierarchyNames,
              products: eligibleProducts
            }}
            uiState={{ 
              showFilters: true,
              shouldDisplayDoubleArrowButton: false,
              appliedLevels,
              selectedLevels,
              typedExpression,
              appliedExpression,
              shoulApplyMargin: false,
              shoulApplyDefaultInlineSize: false
            }}
          />  
        </section>

        <section style={{ display: 'flex', gap: "1rem", justifyContent: "center" }}>
          <Button 
            label="Fechar"
            size="sm"
            type="outlined"
            onClick={handleClose}
          />

          <Button 
            label="Prosseguir"
            size="sm"
            type="filled"
            disabled={!!appliedLevels}
            onClick={() => goToSupplierConfirmation()}
          />          
        </section>
      </div>
    </div>
  );
};

export const SupplierConfirmationModal = ({ actions, ctx, uiState }) => {

  const { 
    onClose = () => {},
    onOrderCreated = () => {},
    setInputs = () => {},
    setAppliedFiltersCount = () => {},
    setSelectedProductIds = () => {},
    setSelectedSupplierFromFilter = () => {},
    setShowFilters = () => {},
    setAppliedLevels,
    setSelectedLevels,
    setTypedExpression,
    setAppliedExpression
  } = actions || {};

  const { 
    suppliersAndProductRelations = [],
    productsWithoutSupplierCount,
    tableInfo,  
    totalProducts,
    selectedSupplierFromFilter = null,
    filteredData,
    prefixes,
    labels,
    products,
    purchaseRecommendationDataOnly,
    hierarchyNames,
    productIds
  } = ctx || {};

  const {
    inputs,
    showInputs,
    saveLoading,
    openOrderConfirmation,
    appliedLevels,
    selectedLevels, 
    typedExpression,
    appliedExpression,
    hasValidItemsForOrder
  } = uiState || {};

  const vm = useSupplierConfirmationViewModel({ 
    ctx: { 
      suppliersAndProductRelations, 
      selectedSupplierFromFilter, 
      filteredData,
      totalProducts,
      productsWithoutSupplierCount,
    },
    uiState: { openOrderConfirmation, appliedLevels }
  });

  const handleClose = () => onClose();

  if (!openOrderConfirmation) return null;
  if (vm.uiState.currentStep === null) return null;

  /** Step: componente PurchaseFlow
   * Representa o fluxo do processo de compra principal
   */

  if (vm.uiState.isPurchaseFlow) {
    return (
      <PurchaseFlow
        actions={{
          setAppliedFiltersCount,
          setSelectedProductIds,
          setSelectedSupplierFromFilter,
          goToSupplierConfirmation: vm.actions.goToSupplierConfirmation,
          handleClose,
          setShowFilters,
          setAppliedLevels, 
          setSelectedLevels,
          setTypedExpression,
          setAppliedExpression
        }}
        ctx={{ 
          suppliersAndProductRelations: suppliersAndProductRelations, 
          productIds: productIds, 
          hierarchyNames: hierarchyNames, 
          products: products,
          filteredData: filteredData
        }}
        uiState={{
          appliedLevels,    
          selectedLevels,   
          typedExpression,
          appliedExpression 
        }}
      />      
    );
  }

  /** Step: componente SupplierConfirmationModal
   * 
   * Representa a etapa intermediária entre o processo de compra principal e o processo de compra secundário.
   * Sempre que o usuário for gerar uma ordem de compra esse modal deve ser exibido para:
   * a) Validar qual o fornecedor escolhido para geração do pedido de compra;
   * b) Dados da loja que receberá o pedido;
   * c) Indicação de quantidade de produtos sem fornecedores presentes na tabela de compras;
   */

  return (
    <>
      {!vm.uiState.isOrderCreated && (
        <section>
          <div style={MODAL_STYLE.OVERLAY}>
            <div style={MODAL_STYLE.DEFAULT}>

              <div style={{ alignSelf: 'end' }}>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                >
                  ×
                </button>
              </div>

              <section style={{ display: 'flex', justifyContent: 'space-between' }}>
                <SupplierSelection
                  actions={{ handleSelectChange: vm.actions.handleSelectChange }}
                  ctx={{
                    filteredData,
                    filteredDataSuppliers: vm.ctx.filteredDataSuppliers,
                    uniqueSuppliers: vm.ctx.uniqueSuppliers,
                    labels: labels,
                    multipleSuppliersData: vm?.ctx?.multipleSuppliersData
                  }}
                  uiState={{
                    selectedSupplierFromFilter,
                    selectedSupplierId: vm.ctx.selectedSupplierId,
                    canProceed: vm.uiState.canProceed
                  }}
                />                

                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  paddingInline: '1rem',
                  gap: '0.4rem'
                }}>
                  <div>
                    <span style={{ fontWeight: 'bold' }}>Região: </span>
                    <span>{tableInfo.region}</span>
                  </div>

                  <div>
                    <span style={{ fontWeight: 'bold' }}>Cidade: </span>
                    <span>{tableInfo.city}</span>
                  </div>

                  <div>
                    <span style={{ fontWeight: 'bold' }}>
                      {`${labels.additionalInformationUpperRightCard}: `}
                    </span>
                    <span>{`${tableInfo.name}`}</span>
                  </div>

                  <div>
                    <span style={{ fontWeight: 'bold' }}>CNPJ: </span>
                    <span>{tableInfo.cnpj}</span>
                  </div>

                  <div>
                    <span style={{ fontWeight: 'bold' }}>
                      {`${labels.additionalInformationDownRightCard}: `}
                    </span>
                    <span>{tableInfo.address}</span>
                  </div>
                </div>
              </section>

              {productsWithoutSupplierCount !== 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <CustomText color="red">
                    {labels.principalMessage}
                  </CustomText>

                  <CustomText color="red">
                    {`${labels.additionalInformationWarning}: ${productsWithoutSupplierCount} de ${totalProducts}`}
                  </CustomText>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button
                  type="outlined"
                  onClick={handleClose}
                  label={labels.firstButtonLabel}
                />

                <Button
                  onClick={() => vm.actions.proceedToOrderModal()}
                  label={labels.secondButtonLabel}
                  disabled={!vm.uiState.canProceed}
                />
              </div>
            </div>
          </div>

          {vm.uiState.openOrderModal && vm.ctx.orderData && (
            <OrderModal
              actions={{
                onClose: () => vm.actions.setOpenOrderModal(false),
                onOrderComplete: (order) => {
                  vm.actions.setOpenOrderModal(false);
                  vm.actions.goToOrderCreated({
                    ...order,
                    supplierName: vm.ctx.orderData?.selectedSupplier?.name
                  });
                }
              }}
              ctx={{
                orderData: vm.ctx.orderData,
                prefixes,
                purchaseRecommendationDataOnly
              }}
              uiState={{
                inputs,
                setInputs,
                showInputs,
                saveLoading
              }}
            />
          )}
        </section>
      )}

      {vm.uiState.isOrderCreated && vm.ctx.createdOrder && (
        <OrderCreatedModal
          actions={{
            onClose: () => {
              onOrderCreated();
              handleClose();
            }
          }}
          ctx={{
            orderNumber: vm.ctx.createdOrder?.orderId,
            supplierName: vm.ctx.createdOrder?.supplierName
          }}
          uiState={{
            isOpen: true
          }}
        />
      )}
    </>
  );
};

SupplierConfirmationModal.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};

OrderModal.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};